import * as React from 'react';
import { isTerminalStatus, OutcomeReason, SubmissionStatus, todayIso } from '@clawhire/shared';
import type {
  OutcomeReason as OutcomeReasonCode,
  SubmissionStatus as SubmissionStatusCode,
} from '@clawhire/shared';
import { enumOptions } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { DateInput } from '@/components/ui/date-input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { EnumBadge } from '@/components/data/EnumBadge';
import type { StatusChangeInput } from '@/pages/submissions/types';

export interface StatusChangeRequest {
  id: string;
  /** "DC-C-… → 2600754 Role", shown in the dialog. */
  title: string;
  from: SubmissionStatusCode;
  to: SubmissionStatusCode;
}

const REASON_OPTIONS = enumOptions(
  OutcomeReason,
  OutcomeReason.codes.filter((c) => c !== 'PLACED'),
);

export interface StatusChangeDialogProps {
  request: StatusChangeRequest | null;
  onConfirm: (input: StatusChangeInput) => Promise<unknown>;
  onCancel: () => void;
}

/**
 * Collects what a status change needs before `POST /submissions/:id/status`:
 * the outcome reason for Rejected/Withdrawn (fixed to "Placed" for Placed), the sent-on date
 * for Submitted, and an optional note that goes into the activity log.
 */
export function StatusChangeDialog({ request, onConfirm, onCancel }: StatusChangeDialogProps) {
  const [reason, setReason] = React.useState<OutcomeReasonCode | null>(null);
  const [sentOn, setSentOn] = React.useState<string | null>(null);
  const [note, setNote] = React.useState('');
  const [busy, setBusy] = React.useState(false);

  const to = request?.to;
  const terminal = to ? isTerminalStatus(to) : false;
  const placed = to === 'PLACED';
  const submitted = to === 'SUBMITTED';

  React.useEffect(() => {
    if (request) {
      setReason(request.to === 'PLACED' ? 'PLACED' : null);
      setSentOn(request.to === 'SUBMITTED' ? todayIso() : null);
      setNote('');
      setBusy(false);
    }
  }, [request]);

  const reasonMissing = terminal && !placed && !reason;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!request || reasonMissing) return;
    setBusy(true);
    try {
      const input: StatusChangeInput = { status: request.to };
      if (terminal) input.outcomeReason = placed ? 'PLACED' : reason;
      if (submitted && sentOn) input.sentOn = sentOn;
      if (note.trim()) input.note = note.trim();
      await onConfirm(input);
    } catch {
      /* the caller shows the error; keep the dialog open so the user can retry */
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={request !== null} onOpenChange={(open) => (open ? undefined : onCancel())}>
      <DialogContent>
        {request ? (
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Move to {SubmissionStatus.label(request.to)}</DialogTitle>
              <DialogDescription className="flex flex-wrap items-center gap-1.5">
                <span className="truncate">{request.title}</span>
                <span className="inline-flex items-center gap-1">
                  <EnumBadge def={SubmissionStatus} value={request.from} size="sm" />
                  <span aria-hidden="true">→</span>
                  <EnumBadge def={SubmissionStatus} value={request.to} size="sm" />
                </span>
              </DialogDescription>
            </DialogHeader>

            {terminal ? (
              placed ? (
                <p className="text-sm">
                  Outcome reason: <span className="font-medium">Placed</span>{' '}
                  <span className="text-muted-foreground">(fixed for placements)</span>. The candidate&apos;s
                  pool status becomes Placed and the position is Filled.
                </p>
              ) : (
                <fieldset className="space-y-2">
                  <legend className="text-sm font-medium">
                    Outcome reason <span className="text-destructive">*</span>
                  </legend>
                  <div role="radiogroup" aria-label="Outcome reason" className="grid gap-1.5 sm:grid-cols-2">
                    {REASON_OPTIONS.map((o) => (
                      <label
                        key={o.value}
                        className="flex cursor-pointer items-center gap-2 rounded-md border px-3 py-2 text-sm hover:bg-accent/60 has-[:checked]:border-primary has-[:checked]:bg-accent/60"
                      >
                        <input
                          type="radio"
                          name="outcome-reason"
                          value={o.value}
                          checked={reason === o.value}
                          onChange={() => setReason(o.value)}
                          className="accent-primary"
                        />
                        {o.label}
                      </label>
                    ))}
                  </div>
                  {request.to === 'REJECTED' ? (
                    <p className="text-xs text-muted-foreground">
                      Rejected clears the waiting-on, next step and follow-up date.
                    </p>
                  ) : null}
                </fieldset>
              )
            ) : null}

            {submitted ? (
              <div className="space-y-1.5">
                <Label htmlFor="status-sent-on">Sent on</Label>
                <DateInput id="status-sent-on" value={sentOn} onChange={setSentOn} />
                <p className="text-xs text-muted-foreground">
                  Feedback due is computed from this date; waiting on defaults to Intermediary.
                </p>
              </div>
            ) : null}

            <div className="space-y-1.5">
              <Label htmlFor="status-note">Note (optional)</Label>
              <Textarea
                id="status-note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
                maxLength={2000}
                placeholder="Goes into the activity log, e.g. feedback from the client."
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={onCancel} disabled={busy}>
                Cancel
              </Button>
              <Button
                type="submit"
                variant={terminal && !placed ? 'destructive' : 'default'}
                loading={busy}
                disabled={reasonMissing}
              >
                Move to {SubmissionStatus.label(request.to)}
              </Button>
            </DialogFooter>
          </form>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
